import { apiJson, apiMultipart } from "../utils/api.js";
import { handleApiError } from "../utils/errorHandler.js";

/**
 * Get order data (paginated, server-side filters)
 * @param {string} chefId - Chef ID
 * @param {Object} [query] - Query parameters for GET kms/order/chef/:id
 * @param {string} [query.status] - Comma-separated order statuses (e.g. "REQUESTED,PREPARING")
 * @param {string} [query.paymentStatus] - Comma-separated payment statuses (e.g. "PAYED,NOT_PAYED")
 * @param {(number|string)[]} [query.companyId] - One or more partner company ids (repeated query param)
 * @param {string} [query.orderNumber] - Case-insensitive substring of order.number
 * @param {string} [query.deliveryDateFrom] - ISO date-time (LocalDateTime)
 * @param {string} [query.deliveryDateTo] - ISO date-time (LocalDateTime)
 * @param {number} [query.limit] - Page size
 * @param {number} [query.offset=0] - Rows to skip
 * @param {string} [query.sortOrder="desc"] - deliveryDateTime sort order ("asc" = nearest first); applied across pages and within each page
 * @returns {Promise<{ orderDtoTreeSet?: Array, count?: number }>} API body
 */
export const getOrderData = async (chefId, query = {}) => {
    const {
        status,
        paymentStatus,
        companyId,
        orderNumber,
        deliveryDateFrom,
        deliveryDateTo,
        limit,
        offset = 0,
        sortOrder = "desc"
    } = query;

    const params = new URLSearchParams();
    if (status != null && status !== "") params.set("status", status);
    if (paymentStatus != null && paymentStatus !== "") params.set("paymentStatus", paymentStatus);
    if (Array.isArray(companyId)) {
        companyId.forEach((id) => {
            if (id != null && String(id).trim() !== "") {
                params.append("companyId", String(id).trim());
            }
        });
    }
    if (orderNumber != null && String(orderNumber).trim() !== "") {
        params.set("orderNumber", String(orderNumber).trim());
    }
    if (deliveryDateFrom != null && deliveryDateFrom !== "") params.set("deliveryDateFrom", deliveryDateFrom);
    if (deliveryDateTo != null && deliveryDateTo !== "") params.set("deliveryDateTo", deliveryDateTo);
    if (limit != null) params.set("limit", String(limit));
    params.set("offset", String(offset));
    params.set("sortOrder", sortOrder);

    const qs = params.toString();
    const url = qs ? `kms/order/chef/${chefId}?${qs}` : `kms/order/chef/${chefId}`;

    try {
        const response = await apiJson.get(url);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3002: Պատվերը չի գտնվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/** Largest page size the UI requests (matches the rows-per-page options); bigger limits fail on the API. */
export const ORDER_PAGE_MAX = 100;
const ORDER_FETCH_MAX_PAGES = 20;

/**
 * Get every order matching the filters by walking pages of ORDER_PAGE_MAX.
 * @param {string} chefId - Chef ID
 * @param {Object} [query] - Same filters as getOrderData; limit/offset are ignored
 * @returns {Promise<{ orderDtoTreeSet: Array, count: number }>}
 */
export const getAllOrderData = async (chefId, query = {}) => {
    const all = [];
    let count = 0;
    for (let page = 0; page < ORDER_FETCH_MAX_PAGES; page++) {
        const res = await getOrderData(chefId, { ...query, limit: ORDER_PAGE_MAX, offset: page * ORDER_PAGE_MAX });
        const batch = res?.orderDtoTreeSet || [];
        count = typeof res?.count === "number" ? res.count : all.length + batch.length;
        all.push(...batch);
        if (batch.length < ORDER_PAGE_MAX || all.length >= count) break;
    }
    return { orderDtoTreeSet: all, count };
};

/**
 * Update order status
 * @param {string} id - Order ID
 * @param {string} status - New status
 * @param {string} reason - Optional reason for status change
 * @returns {Promise<Object>} Updated order data
 */
export const updateOrderStatus = async (id, status, reason = "") => {
    try {
        const params = new URLSearchParams({ status, reason }).toString();
        const response = await apiJson.put(`/kms/order/${id}/update-status?${params}`, {});
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3004: Պատվերի նոր կարգավիճակը անվավեր է: Խնդրում ենք ստուգել կարգավիճակը և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Update order payment status
 * @param {string} id - Order Payment ID
 * @param {string} status - New payment status
 * @returns {Promise<Object>} Updated order payment data
 */
export const updatePaymentStatus = async (id, status) => {
    try {
        const params = new URLSearchParams({ status }).toString();
        const response = await apiJson.put(`/kms/order/payment/${id}/update-status?${params}`, {});
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Վճարման կարգավիճակը անվավեր է: Խնդրում ենք ստուգել և փորձել կրկին:"
        });
        throw error;
    }
};

/**
 * Update order payment status (New endpoint using orderId)
 * @param {string} orderId - Order ID
 * @param {string} status - New payment status
 * @returns {Promise<Object>} Updated order data
 */
export const updateOrderPaymentStatus = async (orderId, status) => {
    try {
        const response = await apiJson.patch(`/kms/order/payment/order/${orderId}/status?status=${status}`, {});
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Վճարման կարգավիճակը անվավեր է: Խնդրում ենք ստուգել և փորձել կրկին:"
        });
        throw error;
    }
};

/**
 * Get order by ID
 * @param {string} id - Order ID
 * @returns {Promise<Object>} Order data
 */
export const getOrderById = async (id) => {
    try {
        const response = await apiJson.get(`/kms/order/${id}`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3002: Պատվերը չի գտնվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Fetch order details from an external marketplace/source (non-BYCHEF).
 * @param {string} orderNumber - External order number (path segment)
 * @param {string} source - Source identifier (query param)
 * @returns {Promise<Object>} Order data
 */
export const getExternalOrder = async (orderNumber, source) => {
    try {
        const params = new URLSearchParams({ source: String(source) }).toString();
        const encoded = encodeURIComponent(String(orderNumber));
        const response = await apiJson.get(`/kms/order/external/${encoded}?${params}`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3002: Պատվերը չի գտնվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};
/**
 * Add order attachment
 * @param {string} orderId - Order ID
 * @param {File} file - File object to upload
 * @returns {Promise<Object>} Updated order data
 */
export const addOrderAttachment = async (orderId, file) => {
    try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await apiMultipart.post(`/kms/order/${orderId}/attachments`, formData);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Չհաջողվեց ավելացնել հավելվածը:"
        });
        throw error;
    }
};

/**
 * Update order attachment
 * @param {string} orderId - Order ID
 * @param {string} attachmentId - Attachment ID
 * @param {File} file - New file object
 * @returns {Promise<Object>} Updated order data
 */
export const updateOrderAttachment = async (orderId, attachmentId, file) => {
    try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await apiMultipart.put(`/kms/order/${orderId}/attachments/${attachmentId}`, formData);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Չհաջողվեց թարմացնել հավելվածը:"
        });
        throw error;
    }
};

/**
 * Delete order attachment
 * @param {string} orderId - Order ID
 * @param {string} attachmentId - Attachment ID
 * @returns {Promise<void>}
 */
export const deleteOrderAttachment = async (orderId, attachmentId) => {
    try {
        await apiJson.delete(`/kms/order/${orderId}/attachments/${attachmentId}`);
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Չհաջողվեց ջնջել հավելվածը:"
        });
        throw error;
    }
};

/**
 * Get active (non-terminal) orders for a specific telegram customer
 * @param {number} customerId - ChefTelegramCustomer ID
 * @returns {Promise<Array>} List of active orders
 */
export const getActiveOrdersByCustomer = async (customerId) => {
    try {
        const response = await apiJson.get(`/kms/chef-bot/customers/${customerId}/active-orders`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Չհաջողվեց բեռնել ակտիվ պատվերները:"
        });
        throw error;
    }
};
